<section class="space-y-6">
  <!-- Read-only notice -->
  <div
    class="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 px-4 py-3 text-sm text-brand-800 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-100">
    <span>Vista de consulta: los turnos se registran y asignan desde la app móvil de CitaLink Pro.</span>
    @if (lastUpdated()) {
    <span class="text-xs text-brand-700/80 dark:text-brand-200/80">Actualizado {{ clock(lastUpdated()) }} · se refresca cada 30 s</span>
    }
  </div>

  @if (isLoading() && !board()) {
  <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
    @for (i of [1, 2, 3, 4]; track i) {
    <div class="h-24 animate-pulse rounded-2xl bg-gray-100 dark:bg-dark-700"></div>
    }
  </div>
  }

  @if (loadError()) {
  <div class="rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-dark-600 dark:bg-dark-800">
    <p class="text-base font-semibold text-gray-800 dark:text-gray-100">No pudimos cargar los turnos</p>
    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Revisa tu conexión e inténtalo de nuevo.</p>
    <button type="button"
      class="mt-4 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
      (click)="load()">Reintentar</button>
  </div>
  }

  @if (board(); as data) {
  <!-- Metrics -->
  <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-600 dark:bg-dark-800">
      <p class="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">{{ data.waitingCount }}</p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">En espera</p>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-600 dark:bg-dark-800">
      <p class="text-3xl font-bold tabular-nums text-green-600 dark:text-green-400">{{ freeCount() }}</p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Profesionales libres</p>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-600 dark:bg-dark-800">
      <p class="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">{{ data.servedToday }}</p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Atendidos hoy por turno</p>
    </div>
    <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-600 dark:bg-dark-800">
      <p class="text-3xl font-bold tabular-nums"
        [class]="longestWait() >= 30 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'">
        {{ longestWait() }} <span class="text-base font-semibold">min</span>
      </p>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Espera más larga
        @if (data.leftToday > 0) {
        · {{ data.leftToday }} se {{ data.leftToday === 1 ? 'fue' : 'fueron' }} hoy
        }
      </p>
    </div>
  </div>

  @if (!data.isOpenNow) {
  <div class="rounded-2xl bg-gray-100 px-4 py-3 text-sm text-gray-700 dark:bg-dark-700 dark:text-gray-200">
    Fuera del horario de atención
    @if (data.nextOpening) {
    · abre a las {{ clock(data.nextOpening) }}
    }
  </div>
  }

  <div class="grid gap-6 xl:grid-cols-5">
    <!-- Professionals now -->
    <div class="xl:col-span-3">
      <div class="mb-3 flex items-baseline justify-between">
        <h2 class="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Profesionales ahora</h2>
        <span class="text-sm tabular-nums text-gray-500 dark:text-gray-400">{{ clock(data.now) }}</span>
      </div>

      @if (data.professionals.length === 0) {
      <div class="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-dark-600 dark:text-gray-400">
        Asigna servicios a tu equipo para poder repartir turnos.
      </div>
      }

      <div class="grid gap-4 sm:grid-cols-2">
        @for (pro of data.professionals; track pro.professionalId) {
        <div class="rounded-2xl border bg-white p-4 shadow-sm dark:bg-dark-800"
          [ngClass]="pro.state === 'free' ? 'border-green-200 dark:border-green-500/30' : 'border-gray-200 dark:border-dark-600'"
          [class.opacity-70]="pro.state === 'blocked' || pro.state === 'closed'">
          <div class="flex items-center gap-3">
            <div class="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
              [style.background-color]="pro.color || '#6366f1'">
              @if (pro.avatarUrl) {
              <img [src]="pro.avatarUrl" [alt]="pro.name" class="h-full w-full rounded-full object-cover" />
              } @else {
              {{ initials(pro.name) }}
              }
              <span class="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white dark:border-dark-800"
                [ngClass]="dotClasses(pro)"></span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="truncate font-semibold text-gray-900 dark:text-white">{{ pro.name }}</p>
              <span class="mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-semibold" [ngClass]="stateClasses(pro)">
                {{ stateLabel(pro) }}
                @if (countdown(pro); as remaining) {
                · <span class="tabular-nums">{{ remaining }}</span>
                }
              </span>
            </div>
          </div>
          <p class="mt-3 text-sm text-gray-600 dark:text-gray-300">{{ stateDetail(pro) }}</p>
          @if (pro.currentEvent?.isWalkIn) {
          <p class="mt-1 text-xs font-medium text-brand-600 dark:text-brand-300">Atención por turno</p>
          }
        </div>
        }
      </div>
    </div>

    <!-- Queue -->
    <div class="xl:col-span-2">
      <div class="mb-3 flex items-baseline justify-between">
        <h2 class="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Fila de espera</h2>
        <span class="text-sm text-gray-500 dark:text-gray-400">{{ data.queue.length }} {{ data.queue.length === 1 ? 'turno' : 'turnos' }}</span>
      </div>

      @if (data.queue.length === 0) {
      <div class="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-dark-600 dark:bg-dark-800">
        <p class="font-semibold text-gray-800 dark:text-gray-100">Nadie esperando</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Cuando alguien llegue sin cita aparecerá aquí con su hora estimada.</p>
      </div>
      }

      <ol class="space-y-3">
        @for (ticket of data.queue; track ticket.walkInTicketId; let first = $first) {
        <li class="rounded-2xl border bg-white p-4 shadow-sm dark:bg-dark-800"
          [ngClass]="first ? 'border-brand-200 dark:border-brand-500/40' : 'border-gray-200 dark:border-dark-600'">
          <div class="flex items-start gap-3">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base font-bold tabular-nums"
              [ngClass]="isReadyNow(ticket) ? 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' : 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-200'">
              {{ ticket.ticketNumber }}
            </span>
            <div class="min-w-0 flex-1">
              <p class="truncate font-semibold"
                [ngClass]="ticket.clientName ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'">
                {{ ticket.clientName || 'Cliente sin nombre' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                {{ ticket.serviceName }} · {{ ticket.durationMinutes }} min
                @if (ticket.preferredProfessionalName) {
                · prefiere a {{ ticket.preferredProfessionalName.split(' ')[0] }}
                }
              </p>
              <p class="text-sm" [ngClass]="waitClasses(ticket)">
                Llegó {{ clock(ticket.arrivedAt) }} · {{ waitLabel(ticket) }}
              </p>
            </div>
            <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold"
              [ngClass]="isReadyNow(ticket) ? 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' : 'bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-gray-200'">
              {{ estimateLabel(ticket) }}
            </span>
          </div>
        </li>
        }
      </ol>
    </div>
  </div>
  }
</section>
